import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useId,
  useInteractions,
  useRole,
  type MiddlewareState,
  type Placement,
} from '@floating-ui/react';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import { Fragment } from 'react';

const Content = styled('div', {
  base: {
    backgroundColor: 'background.canvas',
    borderRadius: '2xl',
    zIndex: 'popover',
    shadow: 'md',
  },
});

const Trigger = styled('span', {
  base: {
    display: 'inline-block',
  },
});

const Spacer = styled('div', {
  variants: {
    spacing: {
      large: {
        py: 'md',
        px: 'lg',
      },
      normal: {
        py: 'sm',
        px: 'md',
      },
    },
  },
});

export type PopoverProps = {
  children: React.ReactNode;
  closeOnFocusOut?: boolean;
  returnFocus?: boolean;
  offset?: [number, number];
  onChange: (open: boolean) => void;
  open: boolean;
  placement?: Placement;
  spacing?: 'normal' | 'large';
  style?: React.CSSProperties;
  target: React.ReactNode;
  sameWidthAsTarget?: boolean;
  minWidth?: number;
  maxWidth?: number;
  withPortal?: boolean;
};

export const Popover = ({
  children,
  closeOnFocusOut = true,
  returnFocus = false,
  offset: offsetAxis = [0, 0],
  onChange,
  open,
  placement,
  spacing = 'normal',
  style,
  target,
  sameWidthAsTarget,
  minWidth,
  maxWidth,
  withPortal,
}: PopoverProps) => {
  const {
    refs: { setReference, setFloating },
    elements: { domReference },
    floatingStyles,
    context,
  } = useFloating({
    open,
    onOpenChange: onChange,
    placement,
    strategy: 'fixed',
    middleware: [
      offset({ mainAxis: offsetAxis[1], crossAxis: offsetAxis[0] }),
      flip({
        fallbackAxisSideDirection: 'end',
      }),
      shift(),
      sameWidthAsTarget &&
        size({
          apply(state: MiddlewareState) {
            state.elements.floating.style.width = `${state.rects.reference.width}px`;
          },
        }),
      typeof minWidth === 'number' &&
        size({
          apply(state: MiddlewareState) {
            state.elements.floating.style.minWidth = `${minWidth}px`;
          },
        }),
      typeof maxWidth === 'number' &&
        size({
          apply(state: MiddlewareState) {
            state.elements.floating.style.maxWidth = `${maxWidth}px`;
          },
        }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const focus = useFocus(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    focus,
    role,
  ]);

  const headingId = useId();

  // If Popover uses `withPortal` and is used inside a Modal, the Modal itself must be used as the root element.
  const modalRoot = (() => {
    if (!withPortal || !open) return undefined;

    return domReference?.closest<HTMLDialogElement>('p-modal') ?? undefined;
  })();

  const MaybePortal = withPortal ? FloatingPortal : Fragment;

  return (
    <>
      <Trigger ref={setReference} style={style} {...getReferenceProps()}>
        {target}
      </Trigger>

      {open && (
        <MaybePortal {...(withPortal && { root: modalRoot })}>
          <FloatingFocusManager
            context={context}
            modal={false}
            returnFocus={returnFocus}
            closeOnFocusOut={closeOnFocusOut}
          >
            <Content
              aria-labelledby={headingId}
              ref={setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <Spacer spacing={spacing}>{children}</Spacer>
            </Content>
          </FloatingFocusManager>
        </MaybePortal>
      )}
    </>
  );
};
